// Norwegian (Bokmål, du) copy for /web-uploader, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "Nettopplasteren" for the web uploader; "forhåndsvisninger" and "originaler". 10 000 with a non-breaking space.
// The uploader's own labels are in English, so they're quoted as it shows them («Previews», «Originals»).
// The text inside the two animations stays in English, like the screenshots (the uploader is in English);
// their captions, legends and accessible names are translated.
import type { WebUploaderCopy } from "./copy.en";
import en from "./copy.en";

const no: WebUploaderCopy = {
  seo: {
    title: "Nettopplasteren fra Photohawk | Raskere enn FTP",
    description:
      "Vi har bygget nettopplasteren til Photohawk på nytt. Den sender mange bilder samtidig, takler opplastinger på over 100\u00a0000 bilder og fortsetter av seg selv hvis forbindelsen brytes, på alle abonnementer. På alle abonnementer lastes lette forhåndsvisninger opp først, så galleriet ditt er på nett på minutter, ikke timer.",
  },
  hero: {
    badge: "Nyhet",
    eyebrow: "På alle abonnementer",
    title: "Den nye nettopplasteren fra Photohawk",
    subtitle:
      "Raskere enn FTP, laget for opplastinger på over 100\u00a0000 bilder, og den fortsetter der den slapp.",
    plans: "På alle abonnementer gjør forhåndsvisninger først at et stort arrangement er på nett på minutter, ikke timer.",
    ctaPrimary: "Åpne nettopplasteren",
    ctaSecondary: "Se hva som er nytt",
    screenshotAlt:
      "Nettopplasteren midt i en opplasting. Med forhåndsvisninger først (alle abonnementer) viser statuslinjen at galleriet er på nett med forhåndsvisninger og hvor mange som besøker det, og køen til høyre viser forhåndsvisningene som er ferdige og originalene som er på vei, med to kjøpte bilder som går først.",
    phoneAlt: "Nettopplasteren på en mobil, åpnet i en mappe i galleriet, med opplastingsstatusen nederst.",
  },
  previews: {
    heading: "Forhåndsvisninger først",
    badge: "Alle abonnementer",
    subtitle:
      "På alle abonnementer: på nett på minutter, ikke timer. Et galleri med 10\u00a0000 bilder kan være på nett og selge etter omtrent 8 minutter med 20 Mbps opplastingshastighet.",
    body: "Lette forhåndsvisninger lastes opp først, så galleriet ditt kan gå på nett og begynne å selge, eller gi gratis nedlastinger, mens originalene kommer etter. Hvis noen kjøper eller laster ned et bilde før originalen har kommet, går den originalen først i køen.",
    steps: [
      { id: "previews", text: "Lette forhåndsvisninger lastes opp først" },
      { id: "live", text: "Galleriet ditt går på nett" },
      { id: "buy", text: "Noen kjøper et bilde" },
      { id: "jump", text: "Originalen går først i køen" },
      { id: "originals", text: "Originalene kommer etter" },
    ],
    anim: {
      ...en.previews.anim,
      label: "Animert illustrasjon av forhåndsvisninger først",
      description:
        "Lette forhåndsvisninger av seks bilder flyr fra datamaskinen din til galleriet ditt, og galleriet skifter til «Live». Noen trykker «Buy» på ett bilde: originalen flyttes først i køen av originaler og lastes opp først. De andre originalene kommer etter.",
    },
    link: "Slik fungerer opplasting med forhåndsvisninger først",
  },
  faster: {
    heading: "Hvorfor den er raskere enn FTP",
    subtitle: "De samme bildene, sendt på to måter.",
    anim: {
      ...en.faster.anim,
      label: "Animert sammenligning av et FTP-program og nettopplasteren",
      description:
        "To rader. Øverst sender et FTP-program bilder 2 om gangen som standard, og aldri mer enn 10. Nederst sender nettopplasteren mange bilder samtidig, i baner som blir bredere og smalere etter forbindelsen. På alle abonnementer går lette forhåndsvisninger først, så galleriet er på nett tidligere. Forbindelsen brytes et øyeblikk, og opplastingen fortsetter av seg selv.",
      legendPreviews: "Forhåndsvisninger",
      legendOriginals: "Originaler",
    },
    points: [
      {
        id: "parallel",
        title: "Mange bilder samtidig",
        body: "Den sender mange bilder samtidig, langt flere enn et FTP-program som FileZilla (2 om gangen som standard, og aldri mer enn 10).",
      },
      {
        id: "adaptive",
        title: "Får mest mulig ut av forbindelsen din",
        body: "Den justerer seg hele tiden mens den laster opp, for å få mest mulig ut av forbindelsen din.",
      },
      {
        id: "scale",
        title: "Laget for store arrangementer",
        body: "Vi har testet den i stor skala, og den takler fint opplastinger på over 100\u00a0000 bilder.",
      },
      {
        id: "live",
        title: "På nett på minutter, ikke timer (alle abonnementer)",
        body: "På alle abonnementer lastes lette forhåndsvisninger opp først, så et galleri med 10\u00a0000 bilder kan være på nett og selge etter omtrent 8 minutter med 20 Mbps opplastingshastighet, mens originalene kommer etter.",
      },
      {
        id: "resume",
        title: "Fortsetter der den slapp",
        body: "Hvis forbindelsen brytes, fortsetter den av seg selv. Legg til mappen på nytt, så sendes bare det som mangler.",
      },
    ],
  },
  /** The upload-time estimator (#estimate). {placeholders} are filled in by the page. */
  estimate: {
    heading: "Hvor lang tid tar opplastingen din?",
    subtitle: "Et anslag for galleriet ditt, ut fra din egen opplastingshastighet.",
    heroLink: "Anslå opplastingstiden din",
    photos: "Antall bilder",
    custom: "Annet",
    customLabel: "Antall bilder (annet)",
    size: "Gjennomsnittlig størrelse på en original",
    sizeUnit: "MB",
    sizeHint: "JPEG-filer fra arrangementer er ofte rundt {mb} MB.",
    speed: "Opplastingshastigheten din",
    speedUnit: "Mbps",
    test: "Test opplastingshastigheten min",
    testing: "Tester … {mbps} Mbps",
    testStarting: "Tester …",
    testDone: "Målt: {mbps} Mbps opplasting",
    testFailed: "Vi fikk ikke målt hastigheten din akkurat nå. Skriv den inn selv, eller prøv en vanlig hastighetstest.",
    dataNote: "Bruker opptil omtrent {mb} MB data, og bare når du trykker på knappen.",
    otherTest: "Eller bruk en vanlig hastighetstest",
    resultsHeading: "Anslaget ditt",
    previewsLabel: "Forhåndsvisninger først",
    previewsPlans: "Alle abonnementer",
    previewsLive: "Galleriet på nett om {time}",
    previewsAll: "Alle originaler lastet opp om {time}",
    originalsLabel: "Bare originaler",
    originalsLive: "Galleriet på nett om {time}",
    originalsWhen: "når alle {gb} GB med originaler er oppe",
    computerNote: "Ved denne hastigheten er det datamaskinen din som setter tempoet: hvor raskt den lager forhåndsvisningene.",
    disclaimer: "Bare anslag. Faktisk tid avhenger av forbindelsen din, datamaskinen din og bildene dine.",
    time: {
      under: "under ett minutt",
      min: "omtrent {n} min",
      h: "omtrent {n} t",
      d: "omtrent {n} dager",
    },
    table: {
      caption: "Eksempel: 10\u00a0000 bilder",
      speed: "Opplastingshastighet",
      live: "Forhåndsvisninger først: galleriet på nett (alle abonnementer)",
      all: "Alle originaler lastet opp",
      note: "Tallet på omtrent 8 minutter ble målt med 20 Mbps opplastingshastighet (forhåndsvisninger først). De andre radene er anslag fra den samme modellen, med originaler på omtrent {mb} MB.",
    },
  },
  features: {
    heading: "Mer som er nytt",
    subtitle: "Ekte skjermbilder fra den nye nettopplasteren.",
    items: [
      {
        id: "status",
        title: "Status med enkle ord",
        body: "Se med ett blikk hvor langt den har kommet. Med forhåndsvisninger først (alle abonnementer) er det «Previews» (forhåndsvisninger), så «Originals» (originaler), hver med sin egen fremdrift.",
        alt: "Opplastingsstatusen med forhåndsvisninger først (alle abonnementer): «Live — saving originals…», én fremdriftslinje for «Previews» og én for «Originals», og «2 photos people bought go first».",
      },
      {
        id: "queue",
        title: "En kø som står der du vil",
        body: "Fest opplastingskøen til høyre, til venstre eller nederst, og dra den til den størrelsen du liker. Nettleseren husker valget ditt.",
        alt: "Opplastingskøen festet nederst i opplasteren, med menyen åpen for å flytte den til høyre, nederst eller til venstre.",
      },
      {
        id: "visitors",
        title: "Se hvem som ser på",
        body: "Statuslinjen viser hvor mange som ser på galleriet ditt akkurat nå, på alle abonnementer.",
        alt: "Statuslinjen øverst i opplasteren: «Live on previews» (med forhåndsvisninger først) og antall besøkende.",
      },
      {
        id: "resume",
        title: "Fortsetter der den slapp",
        body: "Hvis forbindelsen brytes, fortsetter den av seg selv. Legg til mappen på nytt, så sendes bare det som mangler.",
        alt: "Et banner i opplasteren som sier at en mappe ikke ble ferdig lastet opp, med en «Resume»-knapp.",
      },
      {
        id: "phone",
        title: "Oversiktlig på mobilen også",
        body: "På en liten skjerm folder mappestien seg sammen så den får plass, og trykker du på «…», ser du mappene imellom. Da vet du alltid hvor bildene dine havner.",
        alt: "Opplasteren på en mobil: mappestien viser gjeldende mappe, og «…» åpner en liste over mappene over den.",
      },
    ],
  },
  ftp: {
    date: "1. november 2026",
    heading: "Dette endres for FTP 1. november",
    body: "De delte FTP-serverne våre, som alltid er på, stenges 1. november 2026. Bruker du FTP i dag? Bytt før den tid.",
    points: [
      {
        id: "closes",
        title: "Delt FTP stenges 1. november 2026",
        body: "De delte FTP-serverne som alltid er på, slås av 1. november 2026.",
      },
      {
        id: "standard",
        title: "Nettopplasteren er standardmåten å laste opp på",
        body: "Den finnes på alle abonnementer, og du kan bruke den i dag.",
      },
      {
        id: "ondemand",
        title: "Hvis FTP er helt nødvendig",
        body: "På alle abonnementer kan alle i arbeidsområdet ditt starte sin egen FTP-server ved behov fra nettopplasteren, på en personlig adresse som aldri endres. Den slår seg av etter en time uten aktivitet.",
      },
    ],
    helpLink: "Slik fungerer FTP ved behov",
  },
  more: {
    previewFirstTitle: "Slik fungerer opplasting med forhåndsvisninger først",
    previewFirstBody: "På alle abonnementer. Steg for steg: forhåndsvisningene, å gå på nett og hva kjøperne ser.",
  },
  cta: {
    heading: "Prøv den på neste opplasting",
    body: "Den nye nettopplasteren er klar på alle abonnementer. Legg til en mappe og se galleriet ditt gå på nett.",
    button: "Åpne nettopplasteren",
  },
  pause: "Sett animasjonen på pause",
  play: "Spill av animasjonen",
};

export default no;
